import type { SelectItemData } from 'tracking/src/gtm/types/gtm';
import type { SelectItemEvent } from './types';

/**
 * Purpose of this file:
 *
 * Because of a requirement to always keep track of where a product was selected from, we persist the select item event data.
 * That means when we fx send a purchase event, we will also send data about which list the item was originally selected from.
 */

const SELECT_ITEM_STORAGE_KEY = 'SELECT_ITEM_STORAGE_KEY_TRACKING';

/**
 * Persist the select item event data in session storage
 * @param event
 */

export const persistSelectItemData = (event: SelectItemEvent) => {
  const selectItemData: SelectItemEvent[] = JSON.parse(
    sessionStorage.getItem(SELECT_ITEM_STORAGE_KEY) ?? '[]',
  );
  selectItemData.push(event);
  sessionStorage.setItem(SELECT_ITEM_STORAGE_KEY, JSON.stringify(selectItemData));
};

const cache: Record<string, Partial<SelectItemData>> = {};

/**
 * Find the origin of where a product was selected from
 * @param productId
 * @param variantId
 */

export const getSelectItemData = (
  productId?: string,
  variantId?: string,
): Partial<SelectItemData> => {
  const cacheKey = `${productId}-${variantId}`;

  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  const selectItemData: SelectItemEvent[] = JSON.parse(
    sessionStorage.getItem(SELECT_ITEM_STORAGE_KEY) ?? '[]',
  );

  try {
    const lastSelectItemEvent = selectItemData.find((event) => {
      if (
        String(event.ecommerce.items[0].item_id) === productId &&
        String(event.ecommerce.items[0].item_variant) === variantId
      ) {
        return true;
      }
    });

    const data = {
      item_list_id: lastSelectItemEvent?.item_list_id,
      item_list_name: lastSelectItemEvent?.item_list_name,
      item_list_type: lastSelectItemEvent?.item_list_type,
      index: lastSelectItemEvent?.index,
    };

    if (lastSelectItemEvent) {
      cache[cacheKey] = data;
    }

    return data;
  } catch {
    console.error('Tracking error: Error in getSelectItemData', productId, variantId);
    sessionStorage.removeItem(SELECT_ITEM_STORAGE_KEY);
    return {};
  }
};
